import { Add, ExpandMore } from '@mui/icons-material';
import { Box, Button, Collapse, MenuItem, MenuList, Modal, Paper, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { OwnerType, TOwner, TOwnerLink, TOwnershipStructure } from '../types';
import { MAX_DEPTH_LEVEL, MAX_LINK_LEVEL, ownerLabelText } from '../constants';
import {
  buildSubtreeUnder,
  getOwnerName,
  getOwnerTypeIcon,
  getSiblingLinks,
} from '../utils/utils.tsx';
import { styles } from '../styles';
import { useFormContext, useWatch } from 'react-hook-form';
import { linkDepth, subtreeHeight } from '../utils/collisionUtils.ts';
import { commonStyles } from '~/theme/styles/commonStyles.ts';

interface IAddOwnerButtonProps {
  handleAddOwner: (item: OwnerType) => void;
  parentId: string;
  parentLinkId?: string | null;
  level?: number;
  isIndividualParent?: boolean;
  title?: string;
}

export const AddOwnerButton = React.memo(
  ({
    handleAddOwner,
    parentId,
    parentLinkId = null,
    level = 1,
    isIndividualParent,
    title = 'Додати Власника',
  }: IAddOwnerButtonProps) => {
    const { control, getValues, trigger, setValue } = useFormContext<TOwnershipStructure>();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [isNewCollapsed, setIsNewCollapsed] = useState<boolean>(false);
    const [isExistingCollapsed, setIsExistingCollapsed] = useState<boolean>(false);
    const [link, owners] = useWatch({ name: ['link', 'owners'], control });

    const siblingLinks = useMemo(() => getSiblingLinks(link, parentLinkId), [link, parentLinkId]);
    const checkIsUnique = useCallback(
      (type: OwnerType) =>
        siblingLinks.some((item) => {
          const child = owners.find((owner) => owner.id === item.fromNodeId);
          return child?.type === type;
        }),
      [siblingLinks, owners]
    );

    const hasShareGroup = checkIsUnique(OwnerType.SHAREHOLDER_GROUP);
    const hasFreeFloat = checkIsUnique(OwnerType.FREE_FLOAT);
    const hasBeneficiaryWithoutOwnership = checkIsUnique(
      OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP
    );

    const getItemsToAdd = useCallback(() => {
      if (isIndividualParent) return [OwnerType.INDIVIDUAL];
      return Object.keys(OwnerType).filter((item) => {
        switch (item) {
          case OwnerType.SHAREHOLDER_GROUP:
            return !hasShareGroup;
          case OwnerType.FREE_FLOAT:
            return !hasFreeFloat;
          case OwnerType.LEGAL_ENTITY:
            return (level || 0) <= MAX_DEPTH_LEVEL;
          case OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP:
            return !level;

          default:
            return true;
        }
      });
    }, [hasShareGroup, hasFreeFloat, level, isIndividualParent, hasBeneficiaryWithoutOwnership]);

    const existingOwners = useMemo(() => {
      const parentLinkObj = link.find((l) => l.id === parentLinkId);
      const parentDepth = parentLinkObj ? linkDepth(parentLinkObj, link) : 0;
      return owners.filter((owner) => {
        if (
          [
            OwnerType.SHAREHOLDER_GROUP,
            OwnerType.FREE_FLOAT,
            OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP,
          ].includes(owner.type)
        )
          return false;
        if (siblingLinks.some((item) => item.fromNodeId === owner.id)) return false;
        if (isIndividualParent) {
          return owner.type === OwnerType.INDIVIDUAL && parentId !== owner.id;
        } else if (
          !(
            owner.firstName ||
            owner.lastName ||
            owner.fullName ||
            owner.middleName ||
            owner.shortNameUkr
          ) ||
          owner.id === parentId
        ) {
          return false;
        }
        const sourceLink = link.find((l) => l.fromNodeId === owner.id);
        const height = sourceLink ? subtreeHeight(sourceLink.id, link) : 1;
        return parentDepth + height <= MAX_LINK_LEVEL;
      });
    }, [owners, siblingLinks, parentId, isIndividualParent]);

    const handleNewCollapse = () => {
      setIsNewCollapsed((value) => !value);
    };

    const handleExistingCollapse = () => {
      setIsExistingCollapsed((value) => !value);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'add-owner-popover' : undefined;

    const handleNewItemClick = (item: OwnerType) => {
      handleAddOwner(item);
      setAnchorEl(null);
    };

    const handleExistingItemClick = (owner: TOwner) => {
      const allOwners = getValues('owners');
      const allLinks = getValues('link');
      const parentOwnerIndex = allOwners.findIndex((item) => parentId === item.id);
      const ownerIndex = allOwners.findIndex((item) => item.id === owner.id);

      const sourceLink =
        owner.type === OwnerType.LEGAL_ENTITY
          ? allLinks.find((link) => link.fromNodeId === owner.id)
          : undefined;

      const chainContainsOwner = (link: TOwnerLink, ownerId: string) => {
        let current: TOwnerLink | undefined = link;
        while (current) {
          if (current.fromNodeId === ownerId) return true;
          current = allLinks.find((l) => l.id === current!.parentLinkId);
        }
        return false;
      };

      const addedLinks: TOwnerLink[] = [];

      if (!parentLinkId) {
        addedLinks.push(
          ...buildSubtreeUnder(null, owner, parentId, allLinks, siblingLinks.length, sourceLink)
        );
      } else {
        const parentInstances = allLinks.filter((l) => l.fromNodeId === parentId);

        for (const instance of parentInstances) {
          if (allLinks.some((l) => l.parentLinkId === instance.id && l.fromNodeId === owner.id)) {
            continue;
          }

          const isCyclicPlacement = chainContainsOwner(instance, owner.id);

          addedLinks.push(
            ...buildSubtreeUnder(
              instance,
              owner,
              parentId,
              allLinks,
              siblingLinks.length,
              isCyclicPlacement ? undefined : sourceLink
            )
          );
        }
      }

      const nextLinks: TOwnerLink[] = [...allLinks, ...addedLinks];
      setValue('link', nextLinks, { shouldDirty: true });

      setAnchorEl(null);
      setIsNewCollapsed(false);
      setIsExistingCollapsed(false);
      trigger(`owners.${parentOwnerIndex}`);
      trigger(`owners.${ownerIndex}`);
    };

    return (
      <>
        <Button
          aria-describedby={id}
          startIcon={<Add />}
          variant="outlined"
          onClick={handleClick}
          data-testid="add-new-owner-toggle"
        >
          {title}
        </Button>
        <Modal open={open} onClose={handleClose}>
          <Paper sx={styles.addOwnerModalPaper}>
            <Typography variant="h6" sx={styles.addOwnerItem} onClick={handleNewCollapse}>
              <ExpandMore
                sx={{ transform: `rotate(${isNewCollapsed ? 0 : -90}deg)`, cursor: 'pointer' }}
                data-testid="add-new"
              />
              Додати нового
            </Typography>
            <Collapse in={isNewCollapsed}>
              <MenuList>
                {getItemsToAdd()?.map((item) => (
                  <MenuItem
                    sx={styles.menuItem}
                    onClick={() => handleNewItemClick(item as OwnerType)}
                    key={item}
                    data-testid={`${item}`}
                  >
                    {getOwnerTypeIcon(item as OwnerType)}
                    {ownerLabelText[item as keyof typeof ownerLabelText]}
                  </MenuItem>
                ))}
              </MenuList>
            </Collapse>
            <Typography variant="h6" sx={styles.addOwnerItem} onClick={handleExistingCollapse}>
              <ExpandMore
                sx={{ transform: `rotate(${isExistingCollapsed ? 0 : -90}deg)`, cursor: 'pointer' }}
                data-testid="add-existing"
              />
              Додати існуючого
            </Typography>
            <Collapse in={isExistingCollapsed}>
              <MenuList>
                {existingOwners.map((owner) => (
                  <MenuItem
                    sx={styles.menuItem}
                    onClick={() => handleExistingItemClick(owner)}
                    key={owner.id}
                  >
                    {getOwnerTypeIcon(owner?.type as OwnerType)}
                    <Box sx={{ ...commonStyles.columnFlexContainer, gap: 0 }}>
                      <Typography>{getOwnerName(owner)}</Typography>

                      <Typography sx={{ color: 'grey' }}>
                        {owner.type === OwnerType.INDIVIDUAL ? 'РНОКПП' : 'ЄДРПОУ'}:{' '}
                        {owner.identificationNumber || owner.foreignTaxNumber}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </MenuList>
            </Collapse>
          </Paper>
        </Modal>
      </>
    );
  }
);
